import { useState } from 'react'

import type { AccountLedger, Calendar } from '../api/generated/model'
import { FREQUENCY_OPTIONS, slugify, type Draft } from '../lib/cashFlowDraft'
import { EstimationFields } from './EstimationFields'

export function CashFlowForm({
  draft,
  setDraft,
  accounts,
  calendars,
  isNew,
  error,
  busy,
  onSubmit,
  onCancel,
}: {
  draft: Draft
  setDraft: (d: Draft) => void
  accounts: AccountLedger[]
  calendars: Calendar[]
  isNew: boolean
  error: string | null
  busy: boolean
  onSubmit: () => void
  onCancel: () => void
}) {
  const [idTouched, setIdTouched] = useState(!isNew)
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft({ ...draft, [key]: value })

  const account = accounts.find((a) => a.accountId === draft.accountId)

  return (
    <form
      className="cf-form"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      <div className="cf-grid">
        <label>
          <span>Name</span>
          <input
            value={draft.name}
            autoFocus
            onChange={(e) => {
              const name = e.target.value
              setDraft({
                ...draft,
                name,
                // The id follows the name until the user edits it, then stops surprising them.
                id: isNew && !idTouched ? slugify(name) : draft.id,
              })
            }}
            placeholder="Rent"
          />
        </label>

        <label>
          <span>Id</span>
          <input
            value={draft.id}
            disabled={!isNew}
            onChange={(e) => {
              setIdTouched(true)
              set('id', e.target.value)
            }}
            placeholder="rent"
          />
        </label>

        <label>
          <span>Direction</span>
          <select
            value={draft.direction}
            onChange={(e) => set('direction', e.target.value as Draft['direction'])}
          >
            <option value="OUT">Money out</option>
            <option value="IN">Money in</option>
          </select>
        </label>

        <label>
          <span>Amount ({draft.currency})</span>
          <input
            value={draft.amount}
            inputMode="decimal"
            onChange={(e) => set('amount', e.target.value)}
            placeholder="1250.00"
          />
        </label>

        <label>
          <span>Account</span>
          <select
            value={draft.accountId}
            onChange={(e) => {
              const next = accounts.find((a) => a.accountId === e.target.value)
              setDraft({
                ...draft,
                accountId: e.target.value,
                // The amount must be in the account's currency; the server enforces it.
                currency: next?.currency ?? draft.currency,
              })
            }}
          >
            {accounts.map((a) => (
              <option key={a.accountId} value={a.accountId}>
                {a.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Calendar</span>
          <select value={draft.calendarId} onChange={(e) => set('calendarId', e.target.value)}>
            {calendars.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>If it lands on a non-business day</span>
          <select
            value={draft.convention}
            onChange={(e) => set('convention', e.target.value as Draft['convention'])}
          >
            <option value="FOLLOWING">Move to the next business day</option>
            <option value="PRECEDING">Move to the previous business day</option>
            <option value="MODIFIED_FOLLOWING">Next, but stay inside the month</option>
            <option value="UNADJUSTED">Leave it where it falls</option>
          </select>
        </label>

        <label>
          <span>Schedule</span>
          <select value={draft.kind} onChange={(e) => set('kind', e.target.value as Draft['kind'])}>
            <option value="RECURRING">Repeating</option>
            <option value="EXPLICIT">Specific dates</option>
          </select>
        </label>
      </div>

      {draft.kind === 'RECURRING' ? (
        <div className="cf-grid cf-subgrid">
          <label>
            <span>First date</span>
            <input type="date" value={draft.anchor} onChange={(e) => set('anchor', e.target.value)} />
          </label>
          <label>
            <span>Every</span>
            <div className="cf-inline">
              <input
                className="cf-narrow"
                value={draft.interval}
                inputMode="numeric"
                onChange={(e) => set('interval', e.target.value)}
              />
              <select
                value={draft.frequency}
                onChange={(e) => set('frequency', e.target.value as Draft['frequency'])}
              >
                {FREQUENCY_OPTIONS.map((f) => (
                  <option key={f} value={f}>
                    {f.toLowerCase()}
                  </option>
                ))}
              </select>
            </div>
          </label>
          <label>
            <span>Short months</span>
            <select
              value={draft.monthEndRule}
              onChange={(e) => set('monthEndRule', e.target.value as Draft['monthEndRule'])}
            >
              <option value="CLAMP">Keep the day number</option>
              <option value="LAST_DAY_OF_MONTH">Track the end of the month</option>
            </select>
          </label>
          <label>
            <span>Ends</span>
            <select
              value={draft.endKind}
              onChange={(e) => set('endKind', e.target.value as Draft['endKind'])}
            >
              <option value="NEVER">Never</option>
              <option value="ON_DATE">On a date</option>
              <option value="AFTER_OCCURRENCES">After a number of times</option>
            </select>
          </label>
          {draft.endKind === 'ON_DATE' && (
            <label>
              <span>Last date</span>
              <input
                type="date"
                value={draft.endDate}
                onChange={(e) => set('endDate', e.target.value)}
              />
            </label>
          )}
          {draft.endKind === 'AFTER_OCCURRENCES' && (
            <label>
              <span>Number of times</span>
              <input
                value={draft.endCount}
                inputMode="numeric"
                onChange={(e) => set('endCount', e.target.value)}
              />
            </label>
          )}
        </div>
      ) : (
        <label className="cf-full">
          <span>Dates, one per line</span>
          <textarea
            rows={4}
            value={draft.dates}
            onChange={(e) => set('dates', e.target.value)}
            placeholder={'2026-09-15\n2027-03-15'}
          />
        </label>
      )}

      <EstimationFields
        draft={draft.estimation}
        setDraft={(estimation) => set('estimation', estimation)}
        currency={draft.currency}
        fallbackLabel="Amount"
      />

      {account && draft.currency !== account.currency && (
        <p className="cf-error">
          Amount is {draft.currency} but {account.name} is {account.currency}.
        </p>
      )}
      {error && <p className="cf-error">{error}</p>}

      <div className="cf-actions">
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Saving…' : isNew ? 'Add cash flow' : 'Save changes'}
        </button>
      </div>
    </form>
  )
}
